import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";

export default function AccountMenu({ triggerClassName }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  // Hover alone left this menu unreachable on touch: phones have no hover, and
  // the old focus-within never fired because the trigger is the panel's sibling,
  // not a descendant. A tap now toggles it, and hover still works on desktop.
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const items = [
    { to: "/dashboard/download", label: t("accountMenu.download") },
    { to: "/dashboard", label: t("accountMenu.licences") },
    { to: "/dashboard/invoices", label: t("accountMenu.invoices") },
    { to: "/dashboard/settings", label: t("accountMenu.profile") },
  ];

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <div ref={rootRef} className="relative inline-block group">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName || "text-sm text-[#A1A1AA] hover:text-white transition-colors inline-flex items-center gap-1"}
      >
        {t("accountMenu.account")}
        <span aria-hidden="true" className="text-[10px] leading-none">▾</span>
      </button>
      {/* pt-2 bridges the gap between button and panel so hover doesn't drop. */}
      <div className={`absolute right-0 top-full pt-2 w-48 ${open ? "block" : "hidden"} group-hover:block z-50`}>
        <div className="bg-[#0a0a0c] border border-white/10 rounded-lg shadow-2xl py-1">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors"
            >
              {item.label}
            </Link>
          ))}
          <div className="my-1 border-t border-white/5" />
          <button
            type="button"
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-sm text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors"
          >
            {t("accountMenu.logout")}
          </button>
        </div>
      </div>
    </div>
  );
}
